<template>
  <page-view
    :is-loading="loading"
    :plyr-page-view="true"
    :content-height="loading ? 'auto' : pageViewHeight"
    :custom-header-class="loading ? '' : 'plyr-page-view-header-css'"
    :custom-content-class="loading ? '' : 'plyr-page-view-content-css'"
    :custom-container-class="loading ? '' : 'plyr-page-view-container-css'"
  >
    <template #header>
      <base-header>
        <template #actions>
          <v-row @click="showMenu = true" style="cursor: pointer">
            <span
              class="dotsRow"
              :class="{
                dotsActive: showMenu,
              }"
            >
              <span>
                <v-icon size="16"> panda-menu-action </v-icon>
              </span>
            </span>
          </v-row>
          <v-menu
            v-model="showMenu"
            min-width="300px"
            absolute
            offset-y
            :position-x="menuPosition.x"
            :position-y="menuPosition.y"
            :transition="null"
          >
            <v-list dense>
              <v-list-item @click="modalConfirmationOriginalSettings = true">
                <v-list-item-title>
                  <span>{{ $t('message.playerPage.restoreSettings') }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </base-header>
    </template>
    <template #content>
      <div class="grid">
        <!-- embed -->
        <div class="grid__preview">
          <div class="grid__preview--container" v-html="embedPage" />
        </div>

        <div class="grid__control">
          <player-tabs></player-tabs>

          <!-- settings::controls -->
          <portal to="controls-tab" :order="1">
            <div>
              <h3 class="body-bold-base pl-2">
                {{ $t('message.playerPage.controls') }}
              </h3>
              <div class="grid__control--items pb-2">
                <template v-for="(item, index) in controlsItems">
                  <div
                    class="grid__control--item mt-2"
                    :key="`control-item-${index}`"
                  >
                    <simple-svg
                      v-if="item.icon"
                      :src="item.icon"
                      width="16px"
                    />
                    <v-icon v-else>mdi-help</v-icon>
                    <span class="body-regular-small pl-4">{{
                      item.label
                    }}</span>
                    <v-spacer></v-spacer>
                    <v-switch
                      v-model="controls"
                      class="mt-0"
                      :value="item.value"
                      @click="unsavedChanges = true"
                      hide-details
                    />
                  </div>
                </template>
              </div>
            </div>
            <v-divider class="my-6 mx-2"></v-divider>
          </portal>

          <portal to="controls-tab" :order="2">
            <div>
              <h3 class="body-bold-base pt-3 pb-2 pl-2">
                {{ $t('message.pages.video.controlsPage.resources') }}
              </h3>
              <div class="grid__resources--items">
                <template v-for="(item, index) in resourcesItems">
                  <div
                    class="grid__resources--item mt-2"
                    :key="`control-item-${index}`"
                  >
                    <div class="grid__resources--label">
                      <span class="body-regular-small">{{ item.label }}</span>
                      <small class="body-regular-smaller">{{
                        item.description
                      }}</small>
                    </div>
                    <v-spacer></v-spacer>
                    <v-switch
                      v-model="controls"
                      class="mt-0"
                      :value="item.value"
                      @click="unsavedChanges = true"
                      hide-details
                    />
                  </div>
                </template>
              </div>
            </div>
          </portal>

          <!-- settings::language -->
          <portal to="controls-tab" :order="4">
            <div
              class="d-flex flex-row mb-10 pt-2"
              style="align-items: baseline"
            >
              <div style="min-width: 50%">
                <h3 class="body-regular-small nowrap pl-2">
                  {{ $t('message.playerPage.language') }}
                </h3>
              </div>
              <v-spacer></v-spacer>
              <base-select
                v-model="language"
                :items="languages"
                :menu-props="{ top: false, offsetY: true }"
                item-text="name"
                item-value="value"
                dense
                hide-details
                @change="unsavedChanges = true"
              />
              <div class="pr-2"></div>
            </div>
          </portal>

          <!-- settings::colors -->
          <portal to="colors-n-captions-tab" :order="1">
            <v-col cols="12" class="pa-0">
              <div>
                <h3 class="player-subtitle pl-2">
                  {{ $t('message.playerPage.playerColors') }}
                </h3>
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center justify-space-between my-2 pa-2 menu__color"
                  >
                    <div class="d-flex flex-column">
                      <span class="d-flex-inline colorTitle">{{
                        $t('message.playerPage.primaryColor')
                      }}</span>
                      <span class="d-flex-inline describeImpact">{{
                        $t('message.playerPage.primaryColorDesc')
                      }}</span>
                    </div>
                    <div>
                      <v-menu
                        v-model="menuControlsColor"
                        :close-on-content-click="false"
                        offset-x
                        nudge-bottom="105"
                        nudge-left="16"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            :color="controlsColor"
                            :style="setControlsColor"
                            label="asdasd"
                            icon
                            class="rounded-pill border-slate"
                          >
                            <span class="describeHexa">{{
                              controlsColor
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          v-model="controlsColor"
                          :maxlength="max"
                          mode="hexa"
                          hide-mode-switch
                          flat
                          @input="changePlayer"
                          @keydown="changeColors()"
                        />
                      </v-menu>
                    </div>
                  </v-col>
                  <v-divider></v-divider>
                  <v-col
                    cols="12"
                    class="d-flex align-center justify-space-between my-2 pa-2 menu__color"
                  >
                    <div class="d-flex flex-column">
                      <span class="d-flex-inline colorTitle">{{
                        $t('message.playerPage.secondaryColor')
                      }}</span>
                      <span class="d-flex-inline describeImpact">{{
                        $t('message.playerPage.secondaryColorDesc')
                      }}</span>
                    </div>
                    <div>
                      <v-menu
                        v-model="menuPrimaryColor"
                        :close-on-content-click="false"
                        offset-x
                        nudge-bottom="105"
                        nudge-left="16"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            :color="primaryColor"
                            :style="setPrimaryColor"
                            icon
                            class="rounded-pill border-slate"
                          >
                            <span class="describeHexa">{{
                              primaryColor
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          v-model="primaryColor"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          @keydown="changeColors()"
                          :maxlength="max"
                          @input="changePlayer"
                        />
                      </v-menu>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </portal>

          <!-- settings::subtitlesColors -->
          <portal to="colors-n-captions-tab" :order="2">
            <v-col cols="12" class="pa-0">
              <v-divider class="my-6 mx-2"></v-divider>
              <div class="my-5">
                <h3 class="player-subtitle pl-2 pt-2">
                  {{ $t('message.playerPage.subtitleColor') }}
                </h3>
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center justify-space-between my-2 pa-2 menu__color"
                  >
                    <div class="d-flex-inline colorTitle">
                      {{ $t('message.playerPage.subtitleFontColor') }}
                    </div>
                    <div>
                      <v-menu
                        v-model="subtitlesColor.menuFg"
                        :close-on-content-click="false"
                        offset-x
                        nudge-bottom="105"
                        nudge-left="16"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            :color="subtitlesColor.foreground"
                            :style="setSubtitlesFontColor"
                            class="rounded-pill border-slate"
                            icon
                          >
                            <span class="describeHexa">{{
                              subtitlesColor.foreground
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          v-model="subtitlesColor.foreground"
                          :maxlength="max"
                          mode="hexa"
                          hide-mode-switch
                          flat
                          @input="changePlayer"
                          @keydown="changeColors()"
                        />
                      </v-menu>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    class="d-flex align-center justify-space-between my-2 pa-2 menu__color"
                  >
                    <div class="d-flex-inline colorTitle">
                      {{ $t('message.playerPage.subtitleBackgroundColor') }}
                    </div>
                    <div>
                      <v-menu
                        v-model="subtitlesColor.menuBg"
                        :close-on-content-click="false"
                        offset-x
                        nudge-bottom="105"
                        nudge-left="16"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            :color="subtitlesColor.background"
                            :style="setSubtitlesBgColor"
                            elevation="3"
                            icon
                            class="rounded-pill border-slate"
                          >
                            <span class="describeHexa">{{
                              subtitlesColor.background
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          elevation="3"
                          v-model="subtitlesColor.background"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          @keydown="changeColors()"
                          :maxlength="max"
                          @input="changePlayer"
                        />
                      </v-menu>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    class="d-flex align-center justify-space-between my-2 pa-2"
                  >
                    <div class="colorTitle">
                      {{ $t('message.playerPage.subtitleFontSize') }}
                    </div>
                    <v-col cols="6" class="pa-0">
                      <v-select
                        v-model="subtitleFontSize"
                        hide-details
                        :items="fontSizes"
                        item-text="label"
                        :menu-props="{ top: false, offsetY: true }"
                        outlined
                        dense
                        @change="setCaptionPreview"
                        class="w-50"
                      ></v-select>
                    </v-col>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </portal>

          <!-- settings::marketing -->
          <portal to="marketing-tab">
            <div>
              <h3 class="body-bold-base pl-2">Pixels</h3>
              <div class="pt-0 pl-2">
                <div class="mb-3">
                  <div
                    class="mb-3"
                    :style="{
                      display: 'grid',
                    }"
                  >
                    <p class="body-regular-smaller mb-0">
                      {{
                        $t('message.pages.videoPlayerPixels.descriptionPixels')
                      }}
                    </p>
                    <a
                      target="_blank"
                      type="secondary"
                      :href="
                        $t(
                          'message.pages.videoPlayerPixels.hrefHowInstallPixel'
                        )
                      "
                    >
                      <span class="body-bold-small">
                        {{ $t('message.playerPage.playerTabs.learnMore') }}
                      </span>
                      <v-icon class="body-bold-small" size="12"
                        >mdi-open-in-new</v-icon
                      >
                    </a>
                  </div>
                  <base-btn
                    type="secondary"
                    size="small"
                    class="my-3"
                    v-if="isMobileBreakpoint"
                    @click="openModalPixel = true"
                    outlined
                  >
                    <v-icon>mdi-plus</v-icon>
                    {{ $t('message.pages.videoPlayerPixels.addPixel') }}
                  </base-btn>
                  <v-col>
                    <v-row
                      class="justify-space-between"
                      v-for="(pixel, index) in pixels"
                      :key="pixel.id"
                    >
                      <div
                        v-if="pixel && pixel.type"
                        class="d-flex flex-wrap align-center"
                        style="gap: 10px"
                      >
                        <img :src="iconPixels[pixel.type]" alt="Pixel Icon" />
                        <span>{{ pixel.id || pixel.name }}</span>
                      </div>
                      <div
                        v-if="pixel && pixel.type"
                        class="d-flex align-center"
                      >
                        <base-btn
                          type="neutral"
                          class="panda-edit icon-neutral-40-light"
                          icon="panda-edit"
                          color="neutral-light"
                          style="font-size: 20px"
                          @click="editPixel(pixel, index)"
                        />
                        <v-menu offset-y right>
                          <template v-slot:activator="{ on, attrs }">
                            <base-btn
                              type="neutral"
                              class="panda-menu-action --pixel"
                              v-bind="attrs"
                              v-on="on"
                              color="neutral-light"
                            />
                          </template>
                          <v-list>
                            <v-list-item>
                              <v-list-item-title>
                                <v-btn
                                  text
                                  class="text-none"
                                  style="color: red"
                                  @click="removePixel(index)"
                                  >{{ $t('message.pages.video.remove') }}</v-btn
                                >
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </v-row>
                  </v-col>
                </div>
                <base-btn
                  v-if="!isMobileBreakpoint"
                  type="secondary"
                  @click="openModalPixel = true"
                  outlined
                >
                  <v-icon>mdi-plus</v-icon>
                  {{ $t('message.pages.videoPlayerPixels.addPixel') }}
                </base-btn>
              </div>
            </div>
          </portal>

          <!-- settings::affiliate -->
          <portal to="affiliate-tab">
            <div>
              <base-alert
                v-if="pandaBranding && isPandaBrandingBonusEnabled"
                class="mb-8 mt-2 mx-2"
                type="success"
                :title="
                  $t(
                    'message.pages.video.controlsPage.logo.enabled.logoAlertTitle'
                  )
                "
                :description="
                  $t(
                    'message.pages.video.controlsPage.logo.enabled.logoAlertDescription'
                  )
                "
              ></base-alert>
              <base-alert
                v-else-if="!pandaBranding && pandaBrandingAction === null"
                class="mb-8 mt-2 mx-2"
                type="informative"
                :title="
                  $t(
                    'message.pages.video.controlsPage.logo.disabled.logoAlertTitle'
                  )
                "
                :description="
                  $t(
                    'message.pages.video.controlsPage.logo.disabled.logoAlertDescription'
                  )
                "
              ></base-alert>
            </div>
            <div>
              <h3 class="body-bold-base pa-2">
                {{ $t('message.pages.video.controlsPage.logo.logoTitle') }}
              </h3>
              <div class="grid__resources--items">
                <div class="grid__resources--item my-3">
                  <div class="grid__resources--label">
                    <span class="body-regular-small">{{
                      $t('message.pages.video.controlsPage.logo.logoLabel')
                    }}</span>
                    <small
                      v-if="isPandaBrandingBonusEnabled || !pandaBranding"
                      class="body-regular-smaller"
                      >{{
                        $t(
                          'message.pages.video.controlsPage.logo.logoDescription'
                        )
                      }}</small
                    >
                    <a
                      v-if="isPandaBrandingBonusEnabled || !pandaBranding"
                      target="_blank"
                      type="secondary"
                      :href="pandaLogoArticle"
                    >
                      <span class="body-bold-smaller">
                        {{
                          $t('message.pages.video.controlsPage.logo.learnMore')
                        }}
                      </span>
                      <v-icon class="body-bold-smaller" size="12"
                        >mdi-open-in-new</v-icon
                      >
                    </a>
                  </div>
                  <v-spacer></v-spacer>
                  <v-switch
                    v-model="pandaBranding"
                    class="mt-0 pt-0"
                    hide-details
                    @click="
                      ;(unsavedChanges = true), (formDisable = !formDisable)
                    "
                    @change="handlePandaBrandingAction($event)"
                  />
                </div>
              </div>
            </div>
            <v-divider class="my-6 mx-2"></v-divider>
            <div>
              <h3 class="body-bold-base my-3 pt-1 px-2">
                {{
                  $t(
                    'message.pages.video.controlsPage.affiliate.AffiliateTitle'
                  )
                }}
              </h3>
              <div class="grid__resources--items">
                <div class="grid__resources--item affiliate my-2">
                  <div
                    class="grid__resources--label"
                    style="min-width: 60%; min-height: 90px"
                  >
                    <span class="body-regular-small">{{
                      $t(
                        'message.pages.video.controlsPage.affiliate.AffiliateLabel'
                      )
                    }}</span>
                    <small
                      class="body-regular-smaller"
                      style="max-width: 80%"
                      >{{
                        $t(
                          'message.pages.video.controlsPage.affiliate.AffiliateDescription'
                        )
                      }}</small
                    >
                    <a
                      target="_blank"
                      type="secondary"
                      :href="pandaAffiliateArticle"
                    >
                      <span class="body-bold-smaller">
                        {{
                          $t(
                            'message.pages.video.controlsPage.affiliate.learnMore'
                          )
                        }}
                      </span>
                      <v-icon class="body-bold-smaller" size="12"
                        >mdi-open-in-new</v-icon
                      >
                    </a>
                  </div>
                  <v-spacer></v-spacer>
                  <v-tooltip max-width="184px" :disabled="!formDisable" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <base-input
                        v-model="refCode"
                        :disabled="formDisable"
                        :placeholder="
                          $t(
                            'message.pages.video.controlsPage.logo.placeholderCode'
                          )
                        "
                        class="mt-0 affiliate-text-input"
                        type="text"
                        dense
                        outlined
                        :hide-details="!refCode"
                        :rules="[rules.checkRefCodeFormat]"
                        @keydown="setRefCode"
                        @input="handleRefCodeFormat"
                        @keydown.space.prevent
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>
                      {{
                        $t('message.playerPage.affiliateInputDisabled')
                      }}</span
                    >
                  </v-tooltip>
                </div>
              </div>
            </div>
          </portal>

          <!-- settings::save-progress -->
          <portal to="controls-tab" :order="3">
            <div>
              <div class="grid__resources--items">
                <div class="grid__resources--item my-2">
                  <div class="grid__resources--label">
                    <span class="body-regular-small">{{
                      $t('message.pages.video.controlsPage.saveProgress.label')
                    }}</span>
                    <small class="body-regular-smaller">{{
                      $t(
                        'message.pages.video.controlsPage.saveProgress.description'
                      )
                    }}</small>
                  </div>
                  <v-spacer></v-spacer>
                  <v-switch
                    v-model="saveProgress"
                    class="mt-0"
                    hide-details
                    @click="unsavedChanges = true"
                  />
                </div>
              </div>
            </div>
          </portal>
        </div>
        <div class="grid__spacer spacer"></div>
        <!-- plyr::actions -->
        <div class="grid__footer d-flex flex-column actions-column">
          <div class="divider-div">
            <v-divider></v-divider>
          </div>
          <div class="d-flex flex-row actions-row">
            <v-spacer></v-spacer>
            <v-tooltip max-width="184px" :disabled="wasChangedSomething" top>
              <template v-slot:activator="{ on, attrs }">
                <div class="pl-2" v-bind="attrs" v-on="on">
                  <base-btn
                    :disabled="
                      loadingButtonOriginalSettings || !wasChangedSomething
                    "
                    type="tertiary"
                    @click="discardChangesModal = true"
                    v-if="hasEditVideoPerm"
                  >
                    {{ $t('message.actions.cancel') }}
                  </base-btn>
                </div>
              </template>
              <span>{{ $t('message.noChanges') }}</span>
            </v-tooltip>
            <v-tooltip max-width="184px" :disabled="wasChangedSomething" top>
              <template v-slot:activator="{ on, attrs }">
                <div class="pl-2" v-bind="attrs" v-on="on">
                  <base-btn
                    :disabled="
                      !validRefCodeFormat ||
                      loadingButtonSave ||
                      !wasChangedSomething
                    "
                    @click="checkPandaBrandingChanges"
                    v-if="hasEditVideoPerm"
                  >
                    {{ $t('message.actions.publish') }}
                  </base-btn>
                </div>
              </template>
              <span>{{ $t('message.noChanges') }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <!-- logoAction: modal -->
      <v-dialog
        v-model="openLogoActionModal"
        persistent
        :width="logoActionModal.width"
        @keydown="handleEvent"
      >
        <v-card class="base-modal__plyr-page" dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="body-bold-medium">
              {{ logoActionModal.title }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="handleCancelPandaBrandingChanges">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text
                  class="body-regular-small"
                  v-html="logoActionModal.description"
                ></v-card-text>
                <v-card-text
                  v-if="logoActionModal.confirmation"
                  class="body-regular-small"
                  ><br />{{ logoActionModal.confirmation }}</v-card-text
                >
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-6"></v-divider>
          <v-card-actions v-if="pandaBrandingAction">
            <v-spacer></v-spacer>
            <base-btn
              type="tertiary"
              text
              @click="handleLogoModalCancel('cancel_action')"
            >
              {{ logoActionModal.cancel }}
            </base-btn>
            <base-btn
              type="primary"
              class="min-with-btn"
              :loading="loadingBtnModalAction"
              @click="handleLogoModalAction('confirm_enabling')"
            >
              {{ logoActionModal.action }}
            </base-btn>
          </v-card-actions>
          <v-card-actions v-else-if="isPandaBrandingBonusEnabled">
            <v-spacer></v-spacer>
            <base-btn
              type="tertiary"
              text
              @click="handleLogoModalCancel('cancel_action')"
            >
              {{ logoActionModal.cancel }}
            </base-btn>
            <base-btn
              type="primary"
              class="min-with-btn"
              :loading="loadingBtnModalAction"
              @click="handleLogoModalAction('confirm_disabling')"
            >
              {{ logoActionModal.action }}
            </base-btn>
          </v-card-actions>
          <v-card-actions v-else>
            <v-spacer></v-spacer>
            <base-btn
              type="tertiary"
              text
              @click="handleLogoModalCancel('confirm_disabling')"
            >
              {{ logoActionModal.cancel }}
            </base-btn>
            <base-btn
              type="primary"
              class="min-with-btn"
              :loading="loadingBtnModalAction"
              @click="handleLogoModalAction('enable_bonus')"
            >
              {{ logoActionModal.action }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="modalConfirmationChanges"
        persistent
        max-width="600px"
        @keydown="handleEvent"
      >
        <v-card class="base-modal__plyr-page" dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="text--subtitle">
              {{ $t('message.playerPage.exitWithoutSaving') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="modalConfirmationChanges = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text class="pl-7 pr-5 py-0">
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text class="pa-0">{{
                  $t('message.playerPage.textConfirmChanges')
                }}</v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-6"></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <base-btn
              type="tertiary"
              text
              @click="modalConfirmationChanges = false"
            >
              {{ $t('message.playerPage.back') }}
            </base-btn>
            <base-btn
              type="primary"
              @click=";(modalConfirmationChanges = false), discardChanges()"
            >
              {{ $t('message.playerPage.discardChanges') }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="modalConfirmationOriginalSettings"
        width="600px"
        @keydown="handleEvent"
      >
        <v-card class="base-modal__plyr-page" dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="body-bold-medium">
              {{ $t('message.playerPage.restoreSettings') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="modalConfirmationOriginalSettings = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text
                  class="body-regular-small"
                  v-html="
                    $t('message.playerPage.restoreSettingsModal.description')
                  "
                ></v-card-text>
                <v-card-text class="body-regular-small"
                  ><br />{{
                    $t('message.playerPage.restoreSettingsModal.confirmation')
                  }}</v-card-text
                >
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-6"></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <base-btn
              type="tertiary"
              class="btn-width--cancel"
              text
              @click="modalConfirmationOriginalSettings = false"
            >
              {{ $t('message.playerPage.restoreSettingsModal.cancel') }}
            </base-btn>
            <base-btn
              type="primary"
              class="btn-width--action"
              @click="
                ;(modalConfirmationOriginalSettings = false),
                  handleOriginalSettings()
              "
            >
              <span class="body-bold-small nowrap">{{
                $t('message.playerPage.restoreSettingsModal.action')
              }}</span>
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- discardChangesModal -->
      <v-dialog
        v-model="discardChangesModal"
        persistent
        width="600px"
        @keydown="handleEvent"
      >
        <v-card class="base-modal__plyr-page" dense>
          <v-toolbar elevation="0">
            <v-toolbar-title class="body-bold-medium">
              {{ $t('message.playerPage.discardChanges') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn icon @click="discardChangesModal = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12">
                <v-card-text
                  >{{ $t('message.playerPage.discardChangesDescription') }}<br
                /></v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-6"></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <base-btn
              class="btn-width--cancel"
              text
              type="tertiary"
              @click="discardChangesModal = false"
            >
              {{ $t('message.cancel') }}
            </base-btn>
            <base-btn
              type="primary"
              class="btn-width--action"
              @click="
                ;(discardChangesModal = false),
                  readPlayer(false),
                  (wasChangedSomething = false)
              "
            >
              <span class="nowrap">{{ $t('message.actions.confirm') }}</span>
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        :fullscreen="isMobileBreakpoint"
        v-model="openModalPixel"
        persistent
        width="800"
        @keydown="handleEvent"
      >
        <v-card
          style="display: flex; flex-direction: column"
          :min-height="isMobileBreakpoint ? '' : 600"
          dense
        >
          <div elevation="0" style="flex: 0; display: flex; padding: 16px 24px">
            <div>
              <v-toolbar-title class="body-bold-medium pt-3">
                {{ $t('message.pages.videoPlayerPixels.addPixel') }}
              </v-toolbar-title>
              <div
                :style="{
                  display: isMobileBreakpoint ? 'grid' : 'flex',
                  gap: '3px',
                  alignItems: isMobileBreakpoint ? 'grid' : 'center',
                }"
              >
                <span class="body-regular-smaller">
                  {{ $t('message.pages.videoPlayerPixels.copyAndPasteCode') }}
                </span>
                <a
                  target="_blank"
                  type="secondary"
                  :href="
                    $t('message.pages.videoPlayerPixels.hrefHowInstallPixel')
                  "
                >
                  <span class="body-bold-smaller">
                    {{ $t('message.pages.videoPlayerPixels.learnInstallMore') }}
                  </span>
                  <v-icon class="body-bold-small" size="16"
                    >mdi-open-in-new</v-icon
                  >
                </a>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn
                icon
                @click="
                  resetPixelForm()
                  openModalPixel = false
                "
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </div>

          <div class="pa-6" style="flex: 1">
            <h3 class="body-bold-base">
              {{ $t('message.pages.videoPlayerPixels.selectPlatform') }}
            </h3>
            <v-sheet
              :style="{
                display: !isMobileBreakpoint ? 'flex' : 'grid',
                justifyContent: !isMobileBreakpoint ? 'space-between' : '',
              }"
              class="mx-auto mt-4"
            >
              {{ $t('message.pages.videoPlayerPixels.platform') }}
              <v-slide-group show-arrows>
                <base-chip
                  width="114"
                  class="text-none rounded-lg"
                  :color="isActiveFbPixelBtn ? 'primary' : null"
                  outlined
                  @click="togglePlatform('meta')"
                >
                  Meta
                </base-chip>
                <base-chip
                  width="114"
                  :color="isActiveGooglePixelBtn ? 'primary' : null"
                  class="ml-2 text-none rounded-lg"
                  outlined
                  @click="togglePlatform('googleAds')"
                >
                  GoogleAds
                </base-chip>
                <!-- <v-btn
                  min-width="115"
                  :color="isActiveCustomPixelBtn ? 'primary' : null"
                  class="text-none rounded-lg px-3"
                  outlined
                  @click="togglePlatform('customPixel')"
                >
                  {{ $t('message.pages.videoPlayerPixels.platforms.custom') }}
                </v-btn> -->
              </v-slide-group>
            </v-sheet>
            <div>
              <v-row v-if="isActiveCustomPixelBtn" class="mt-4" no-gutters>
                <v-col style="color: #282931">
                  {{ $t('message.pages.videoPlayerPixels.placeholders.name') }}
                </v-col>
                <v-col>
                  <v-textarea
                    v-model="pixelData.name"
                    :placeholder="
                      $t('message.pages.videoPlayerPixels.labels.namePixel')
                    "
                    class="mt-0"
                    type="text"
                    outlined
                    no-resize
                    rows="1"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="isActiveCustomPixelBtn"
                class="mt-4 align-center"
                no-gutters
              >
                <v-col style="color: #282931">
                  {{ $t('message.pages.videoPlayerPixels.labels.type') }}
                </v-col>
                <v-col>
                  <v-select
                    outlined
                    hide-details
                    :placeholder="
                      $t('message.pages.videoPlayerPixels.placeholders.select')
                    "
                    :items="
                      typeCustomPixel.map((item) => ({
                        ...item,
                        disabled: item.id === 'absolute',
                      }))
                    "
                    item-text="label"
                    item-value="id"
                    @change="toggleTypeCustomPixel"
                  >
                    <template #item="{ item, attrs }">
                      <v-tooltip bottom v-if="item.disabled">
                        <template #activator="{ on }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                            style="
                              display: flex;
                              align-items: center;
                              cursor: not-allowed;
                            "
                          >
                            <span :class="{ 'text-muted': item.disabled }">{{
                              item.label
                            }}</span>
                          </div>
                        </template>
                        <span>Essa opção está desabilitada por [motivo]</span>
                      </v-tooltip>
                      <span v-else>{{ item.label }}</span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-row
                v-if="
                  isActiveCustomPixelBtn &&
                  pixelData.conditionType == 'percentage'
                "
                class="align-center"
                no-gutters
              >
                <v-col>
                  <span style="color: #282931">{{
                    $t('message.pages.videoPlayerPixels.types.percentage')
                  }}</span>
                </v-col>
                <v-col class="mt-2">
                  <v-slider
                    hide-details
                    :max="100"
                    :step="1"
                    :value="pixelData.conditionValue"
                    @input="updateCustomValue"
                    class="ma-4 d-flex align-center"
                  >
                    <template v-slot:append>
                      <v-textarea
                        style="width: 60px"
                        type="number"
                        outlined
                        :value="pixelData.conditionValue"
                        @input="updateCustomValue"
                        hide-details
                        rows="1"
                        no-resize
                      >
                        <template v-slot:append-inner> % </template>
                      </v-textarea>
                    </template>
                  </v-slider>
                </v-col>
              </v-row>
              <v-divider
                v-if="
                  isActiveFbPixelBtn ||
                  isActiveGooglePixelBtn ||
                  isActiveCustomPixelBtn
                "
                class="my-7"
              ></v-divider>
              <h3
                v-if="
                  isActiveFbPixelBtn ||
                  isActiveGooglePixelBtn ||
                  isActiveCustomPixelBtn
                "
                class="body-bold-base mb-6"
              >
                Pixel
              </h3>
              <v-row
                v-if="isActiveFbPixelBtn || isActiveGooglePixelBtn"
                no-gutters
              >
                <v-col>
                  <v-textarea
                    label="ID"
                    v-model="pixelData.id"
                    :placeholder="
                      isActiveGooglePixelBtn
                        ? $t(
                            'message.pages.videoPlayerPixels.placeholders.id.gtagIds'
                          )
                        : $t(
                            'message.pages.videoPlayerPixels.placeholders.id.fbPixel2'
                          )
                    "
                    class="text-input-pixel mt-0"
                    type="text"
                    dense
                    outlined
                    rows="1"
                    no-resize
                    hide-details="auto"
                    :error-messages="errorMessage"
                    @keydown="setIDPixel"
                    @blur="validateField"
                  />
                </v-col>
              </v-row>
              <v-row v-if="isActiveCustomPixelBtn" no-gutters>
                <v-col>
                  <v-textarea
                    @keydown="setIDPixel"
                    v-model="pixelData.script"
                    placeholder="Adicione seu código aqui"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-actions class="px-6 pt-4 pb-6 justify-space-around">
            <v-spacer v-if="!isMobileBreakpoint"></v-spacer>
            <base-btn
              type="tertiary"
              @click="
                resetPixelForm()
                openModalPixel = false
              "
              :style="{ width: isMobileBreakpoint ? '100%' : 'auto' }"
            >
              {{ $t('message.cancel') }}
            </base-btn>
            <base-btn
              :style="{ width: isMobileBreakpoint ? '100%' : 'auto' }"
              class="ml-5"
              elevation="0"
              :disabled="errorMessage !== ''"
              @click="publishPixel"
            >
              {{ $t('message.pages.videoPlayerPixels.publish') }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </page-view>
</template>

<script>
// external imports
import { mask } from 'vue-the-mask'
import { mapState, mapMutations } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
// internal imports
import playerApi from '@/services/playerApi'
import profileApi from '@/services/profileApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import i18n from '@/translations/lang'
import PlayerTabs from '@/views/Player/PlayerTabs.vue'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import BaseAlert from '@/components/BaseAlert.vue'
import BaseInput from '@/components/BaseComponents/BaseInput.vue'

// components
import PageView from '@/layout/PageView_plyrPage.vue'
import MetaLogo from '@/assets/pandaIcons/metaLogo.svg'
import GoogleAdsLogo from '@/assets/pandaIcons/googleAdsLogo.svg'
import CustomPixelLogo from '@/assets/pandaIcons/customPixel.svg'

//mixins
import logoBonificationMixin from '@/utils/logoBonification.mixin.js'

import BaseChip from '../../components/BaseComponents/BaseChip.vue'
export default {
  name: 'Player',
  directives: { mask },
  components: {
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
    PlayerTabs,
    BaseSelect,
    BaseAlert,
    BaseChip,
    BaseInput,
  },
  mixins: [linkHelper, logoBonificationMixin],
  beforeRouteLeave(to, from, next) {
    if (this.checkChanges() && this.unsavedChanges) {
      this.modalConfirmationChanges = true
      this.to = to
    } else {
      next()
    }
  },
  data: () => ({
    openModalPixel: false,
    subtitleFontSize: 'normal',
    wasChangedSomething: false,
    errorMessage: '',
    subtitlesColor: {
      menuFg: false,
      menuBg: false,
      foreground: '#ffffff',
      background: '#000000',
    },
    video: {
      videoURL:
        'https://b-vz-ded14ebd-85a.tv.pandavideo.com.br/d39148d9-9aa1-4a1e-98a9-4535812e95fe/playlist.m3u8',
      thumb:
        'https://thumbs.tv.pandavideo.com.br/vz-ded14ebd-85a/3b101f05-84aa-4de0-9b64-71f1855388af.png',
    },
    initialPlayer: {
      playerControlsColor: '',
      playerPrimaryColor: '',
      playerControls: [],
      playerSaveProgress: true,
      playerFbPixel: '',
      playerGtagIds: '',
      playerRefCode: '',
      defaultLanguage: '',
    },
    controlsColor: '#000000',
    menuControlsColor: false,
    primaryColor: '#000000',
    menuPrimaryColor: false,
    controls: [],
    saveProgress: true,
    fbPixel: '',
    gaPixel: '',
    refCode: '',
    pixels: [],
    timeVideo: 0,
    indexPixel: null,
    pixelData: {
      id: '',
      type: '',
      name: '',
      conditionType: '',
      conditionValue: '',
      script: '',
    },
    iconPixels: {
      meta: MetaLogo,
      googleAds: GoogleAdsLogo,
      customPixel: CustomPixelLogo,
    },
    typeCustomPixel: [
      {
        label: i18n.t('message.pages.videoPlayerPixels.types.percentage'),
        id: 'percentage',
      },
      {
        label: i18n.t('message.pages.videoPlayerPixels.types.absolute'),
        id: 'absolute',
      },
      {
        label: i18n.t('message.pages.videoPlayerPixels.types.view'),
        id: 'view',
      },
      {
        label: i18n.t('message.pages.videoPlayerPixels.types.play'),
        id: 'play',
      },
    ],
    pandaBranding: true,
    loading: false,
    loadingButtonSave: false,
    loadingButtonOriginalSettings: false,
    modalConfirmationChanges: false,
    modalConfirmationOriginalSettings: false,
    isActiveFbPixelBtn: false,
    isActiveGooglePixelBtn: false,
    isActiveCustomPixelBtn: false,
    unsavedChanges: null,
    to: null,
    max: 7,
    formDisable: false,
    arrayProfiles: [],
    customPixels: [],
    profile: '',
    toggle: true,
    language: '',
    loadings: {
      updatingPixel: false,
      loadingConditionValueIpt: false,
    },
    pixelFontSizes: {
      small: 14,
      normal: 20,
      large: 36,
    },
    facebookArticleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/id-do-facebook-pixel-pj2pyc/',
      en: 'https://help.pandavideo.com/en-us/article/facebook-pixel-id-1hhdur6/',
    },
    googleArticleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/id-do-google-pixel-tf89jl/',
      en: 'https://help.pandavideo.com/en-us/article/google-pixel-id-1wxl9ja/',
    },
    icons: {
      playLarge: require('../../assets/playerTabs/controls/icons/play-large.svg'),
      play: require('../../assets/playerTabs/controls/icons/play.svg'),
      forward: require('../../assets/playerTabs/controls/icons/fast-forward.svg'),
      rewind: require('../../assets/playerTabs/controls/icons/rewind.svg'),
      volume: require('../../assets/playerTabs/controls/icons/volume.svg'),
      captions: require('../../assets/playerTabs/controls/icons/captions.svg'),
      pip: require('../../assets/playerTabs/controls/icons/pip.svg'),
      settings: require('../../assets/playerTabs/controls/icons/settings.svg'),
      smartSearch: require('../../assets/playerTabs/controls/icons/smart-search.svg'),
      airplay: require('../../assets/playerTabs/controls/icons/airplay.svg'),
      cast: require('../../assets/playerTabs/controls/icons/cast.svg'),
      fullscreen: require('../../assets/playerTabs/controls/icons/fullscreen.svg'),
    },
    openLogoActionModal: false,
    pandaBrandingAction: null,
    pageViewHeight: 'calc(100vh - 56px)',
    showMenu: null,
    menuPosition: {
      x: 1920,
      y: 62,
    },
    discardChangesModal: null,
    loadingBtnModalAction: null,
    rules: {
      checkRefCodeFormat: (v) => {
        const pattern = /^[a-z0-9_-]*$/
        return (
          pattern.test(v) ||
          i18n.t('message.pages.video.controlsPage.logo.invalidRefCode')
        )
      },
    },
    validRefCodeFormat: true,
    updateheaderTitleInterval: null,
  }),
  watch: {
    hasEditVideoPerm() {
      this.changePlayer()
    },
    loading: {
      handler(val) {
        if (!val) {
          this.updateheaderTitleInterval = setInterval(() => {
            this.updateheaderTitle()
          }, 150)
        }
      },
    },
  },
  beforeDestroy() {
    clearInterval(this.updateheaderTitleInterval)
  },
  computed: {
    ...mapState({
      userPlan: (state) => state.user.userPlanInfo,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      isPandaBrandingBonusEnabled: (state) =>
        state.user.isPandaBrandingBonusEnabled,
    }),
    hasEditVideoPerm: {
      get() {
        return (
          this.$store.state.user.userPermissions?.includes('put-video') ?? false
        )
      },
      cache: false,
      deep: true,
    },
    fontSizes() {
      return [
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.small'),
          value: 'small',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.normal'),
          value: 'normal',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.large'),
          value: 'large',
        },
      ]
    },
    embedPage() {
      return `<div style="position:relative;padding-top:56.25%;"><iframe id="panda-player" src="https://player-vz-ded14ebd-85a.tv.pandavideo.com.br/embed/?v=c965679f-bbda-4997-962f-d6a501004c10&alternativeProgress=false&mutedIndicatorIcon=false&controls=${this.organizeControls()}${
        this.pandaBranding ? '&pandaBranding=true' : '&pandaBranding=false'
      }${
        this.language ? '&defaultLanguage=' + this.language : ''
      }" style="border:none;position:absolute;top:0;height:100%;width:100%" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"allowfullscreen=true></iframe></div>`
    },
    saveVideoProgressLink() {
      return this.getSaveVideoProgressLink()
    },
    facebookPixelLink() {
      const articleLink =
        this.facebookArticleLinks[this.$i18next.resolvedLanguage] ??
        this.facebookArticleLinks['en']
      return articleLink
    },
    googlePixelLink() {
      const articleLink =
        this.googleArticleLinks[this.$i18next.resolvedLanguage] ??
        this.googleArticleLinks['en']
      return articleLink
    },
    setControlsColor() {
      const { controlsColor, menuControlsColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-video-control-color', controlsColor)
      return {
        backgroundColor: controlsColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuControlsColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setSubtitlesFontColor() {
      let root = document.querySelector(':root')
      root.style.setProperty(
        '--plyr-color-main',
        this.subtitlesColor.foreground
      )
      return {
        backgroundColor: this.subtitlesColor.foreground,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: this.subtitlesColor.menuFg ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setSubtitlesBgColor() {
      let root = document.querySelector(':root')
      root.style.setProperty(
        '--plyr-color-main',
        this.subtitlesColor.background
      )
      return {
        backgroundColor: this.subtitlesColor.background,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: this.subtitlesColor.menuBg ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setPrimaryColor() {
      const { menuPrimaryColor, primaryColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-color-main', primaryColor)
      return {
        backgroundColor: primaryColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuPrimaryColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    controlsItems() {
      this.$i18next.resolvedLanguage
      return [
        {
          label: i18n.t('message.pages.video.bigPlayButton'),
          value: 'play-large',
          icon: this.icons.playLarge,
        },
        {
          label: 'Play/Pause',
          value: 'play',
          icon: this.icons.play,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.forward'),
          value: 'fast-forward',
          icon: this.icons.forward,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.backward'),
          value: 'rewind',
          icon: this.icons.rewind,
        },
        {
          label: 'Volume',
          value: 'volume',
          icon: this.icons.volume,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.captions'),
          value: 'captions',
          icon: this.icons.captions,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.pip'),
          value: 'pip',
          icon: this.icons.pip,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.settings'),
          value: 'settings',
          icon: this.icons.settings,
        },
        // {
        //   label: i18n.t('message.pages.video.controlsPage.smartSearch'),
        //   value: 'smartSearch',
        //   icon: this.icons.smartSearch,
        // },
        {
          label: 'Airplay',
          value: 'airplay',
          icon: this.icons.airplay,
        },
        {
          label: 'Chromecast',
          value: 'cast',
          icon: this.icons.cast,
        },
        {
          label: 'Fullscreen',
          value: 'fullscreen',
          icon: this.icons.fullscreen,
        },
      ]
    },
    resourcesItems() {
      this.$i18next.resolvedLanguage
      return [
        {
          label: i18n.t('message.pages.video.controlsPage.currentTime.label'),
          description: i18n.t(
            'message.pages.video.controlsPage.currentTime.description'
          ),
          value: 'current-time',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.progress.label'),
          description: i18n.t(
            'message.pages.video.controlsPage.progress.description'
          ),
          value: 'progress',
        },
      ]
    },
    languages() {
      return [
        {
          value: '',
          name: this.$i18next.t(
            'message.pages.video.controlsPage.automaticLang'
          ),
        },
        {
          value: 'pt',
          name: this.$i18next.t('message.portuguese'),
        },
        {
          value: 'en',
          name: this.$i18next.t('message.english'),
        },
        {
          value: 'es',
          name: this.$i18next.t('message.spanish'),
        },
      ]
    },
    pandaLogoArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/monthly-bandwidth-bonus-for-activating-the-panda-video-logo-on-the-player-11a6izb/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/bonificacion-de-ancho-de-banda-mensual-por-activar-el-logo-de-panda-video-en-el-player-1qzbm4c/'
      }
      return 'https://help.pandavideo.com/pt-br/article/bonificacao-de-banda-mensal-na-ativacao-do-logo-da-panda-video-no-player-1bhzgfl/'
    },
    pandaAffiliateArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-does-the-pandas-affiliate-program-works-7odqgm/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/en-us/article/how-does-the-pandas-affiliate-program-works-7odqgm/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-funciona-o-programa-panda-partners-be0jrk/'
    },
    logoActionModal() {
      if (this.pandaBrandingAction) {
        return {
          title: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalEnabling.title'
          ),
          description: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalEnabling.description'
          ),
          action: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalEnabling.enable'
          ),
          cancel: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalEnabling.cancel'
          ),
          width: '438px',
        }
      }
      if (this.isPandaBrandingBonusEnabled) {
        return {
          title: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalDisabling.title'
          ),
          description: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalDisabling.description'
          ),
          confirmation: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalDisabling.confirmation'
          ),
          action: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalDisabling.disable'
          ),
          cancel: this.$i18next.t(
            'message.pages.video.controlsPage.logo.actionModalDisabling.cancel'
          ),
          width: '640px',
        }
      }
      return {
        title: this.$i18next.t(
          'message.pages.video.controlsPage.logo.actionModalDisabling.noBonusEnabled.title'
        ),
        description: this.$i18next.t(
          'message.pages.video.controlsPage.logo.actionModalDisabling.noBonusEnabled.description'
        ),
        confirmation: this.$i18next.t(
          'message.pages.video.controlsPage.logo.actionModalDisabling.noBonusEnabled.confirmation'
        ),
        action: this.$i18next.t(
          'message.pages.video.controlsPage.logo.actionModalDisabling.noBonusEnabled.keepLogo'
        ),
        cancel: this.$i18next.t(
          'message.pages.video.controlsPage.logo.actionModalDisabling.noBonusEnabled.disableLogo'
        ),
        width: '527px',
      }
    },
  },
  async created() {
    await this.readPlayer()
    await this.readProfile()
  },
  mounted() {
    window.addEventListener('message', (event) => {
      if (event.data.hasOwnProperty('message')) {
        if (event.data.message === 'panda_ready') {
          this.changePlayer()
        }
      }
      if (!this.profile.colors) return
      const pandaPlayer = document.getElementById('panda-player')

      if (pandaPlayer) {
        const iframe = pandaPlayer.contentWindow
        const { data } = event
        if (data.message === 'panda_ready') {
          iframe.postMessage(
            {
              type: 'colors',
              parameter: {
                colors: {
                  primaryColor: this.primaryColor,
                  controlsColor: this.controlsColor,
                  captionsColor: this.subtitlesColor.foreground,
                  captionsBackgroundColor: this.subtitlesColor.background,
                },
              },
            },
            '*'
          )
          this.setCaptionPreview()
          this.readPlayer(false)
        }
      }
    })
  },
  methods: {
    async handleLogoModalAction(param) {
      if (param === 'confirm_enabling') {
        this.openLogoActionModal = false
        await this.handleEditPlayer()
        this.UPDATE_USER_LOGO_BONUS(true)
        return
      }
      if (param === 'confirm_disabling') {
        this.openLogoActionModal = false
        this.pandaBranding = false
        this.UPDATE_USER_LOGO_BONUS(false)
        this.openLogoActionModal = false
        this.refCode = null
        await this.handleEditPlayer()
        this.pandaBrandingAction = null
        return
      }
      if (param === 'enable_bonus') {
        await this.handleEnablingPandaBrandingBonus()
        return
      }
    },
    async handleLogoModalCancel(param) {
      if (param === 'cancel_action') {
        await this.handleCancelPandaBrandingChanges()
        return
      }
      if (param === 'confirm_disabling') {
        this.openLogoActionModal = false
        this.pandaBranding = false
        this.UPDATE_USER_LOGO_BONUS(false)
        this.refCode = null
        await this.handleEditPlayer()
        return
      }
    },
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_USER_PANDA_BRANDING_STATE',
      'UPDATE_USER_LOGO_BONUS',
    ]),
    removePixel(index) {
      this.pixels.splice(index, 1)
      this.setPixelValue()
      this.checkChanges()
      this.indexPixel = null
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        type: 'success',
        text: i18n.t('message.pages.videoPlayerPixels.toastRemovedPixel'),
      })
    },
    editPixel(pixel, index) {
      this.togglePlatform(pixel.type)
      this.openModalPixel = true
      this.pixelData.id = pixel.id
      this.indexPixel = index
    },
    updateCustomValue(value) {
      this.pixelData.conditionValue = value
    },
    toggleTypeCustomPixel(type) {
      this.pixelData.conditionType = type
      this.handleChangePixelTimeMode(type)
    },
    publishEditPixel(pixel, index) {
      this.pixels[index] = pixel
      this.setPixelValue()
      this.indexPixel = null
    },
    publishPixel() {
      this.validateField()
      if (!this.pixelData.id) return
      if (this.indexPixel == 0 || this.indexPixel)
        this.publishEditPixel(this.pixelData, this.indexPixel)
      else this.pixels.push({ ...this.pixelData })
      this.setPixelValue()
      this.checkChanges()
      this.resetPixelForm()
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        type: 'success',
        text: i18n.t('message.pages.videoPlayerPixels.toastAddPixel'),
      })
      this.openModalPixel = false
      this.isActiveFbPixelBtn = false
      this.isActiveGooglePixelBtn = false
      this.isActiveCustomPixelBtn = false
    },
    openInNewTab(url) {
      window.open(url, '_blank')
    },
    setPixelValue() {
      this.fbPixel = this.pixels
        .filter((pixel) => pixel.type == 'meta')
        .map((pixel) => {
          return this.unmask(pixel.id)
        })
      this.gtagIds = this.pixels
        .filter((pixel) => pixel.type == 'googleAds')
        .map((pixel) => {
          return pixel.id
        })
      this.customPixels = this.pixels
        .filter((pixel) => pixel.type == 'customPixel')
        .map((pixel) => {
          return {
            name: pixel.name,
            script: this.getScriptWithoutScriptTag(pixel.script),
            conditionType: pixel.conditionType,
            conditionValue: pixel.conditionType,
            id: pixel.id ? pixel.id : uuidv4(),
          }
        })
    },
    enableCaptionPreview() {
      this.handlePlayerCaptionPreview('handle')
      const backgroundColor = this.subtitlesColor.background
      const textColor = this.subtitlesColor.foreground
      const fontSize = this.pixelFontSizes[this.subtitleFontSize] + 'px'

      const iframe = document.getElementById('panda-player')
      iframe.contentWindow.postMessage(
        {
          type: 'enable_caption_preview',
          parameter: {
            backgroundColor: backgroundColor,
            textColor: textColor,
            fontSize: fontSize,
          },
        },
        '*'
      )
    },
    setCaptionPreview() {
      const backgroundColor = this.subtitlesColor.background
      const textColor = this.subtitlesColor.foreground
      const fontSize = this.pixelFontSizes[this.subtitleFontSize] + 'px'
      const iframe = document.getElementById('panda-player')
      iframe.contentWindow.postMessage(
        {
          type: 'set_caption_preview',
          parameter: {
            backgroundColor: backgroundColor,
            textColor: textColor,
            fontSize: fontSize,
          },
        },
        '*'
      )
      this.enableCaptionPreview()
      this.checkChanges()
    },
    disableCaptionPreview() {
      const iframe = document.getElementById('panda-player')

      iframe.contentWindow.postMessage(
        {
          type: 'disable_caption_preview',
        },
        '*'
      )
      this.handlePlayerCaptionPreview('undo')
    },
    handlePlayerCaptionPreview(param) {
      const handle = param === 'handle'
      let functionToExecuteOnPlayer

      if (handle) {
        functionToExecuteOnPlayer = function captionPreviewPreSettings() {
          /* eslint-disable no-undef */
          const isPlaying = window.player.playing
          const isCaptionsActive = window.player.captions.active

          function handleCaptionText() {
            const captionElement = document.querySelector(
              '.plyr__caption:not(#caption-preview)'
            )
            if (!captionElement) return
            const innerText = captionElement.innerText
            if (innerText) captionElement.innerText = ''
          }

          if (isPlaying) player.pause()
          if (isCaptionsActive) handleCaptionText()

          const bigPlayButton = document.querySelector(
            '[data-plyr="play"].plyr__control.plyr__control--overlaid'
          )
          if (bigPlayButton) bigPlayButton.style.visibility = 'hidden'
          const plyr = document.querySelector('.plyr')
          setTimeout(() => {
            plyr.classList.add('plyr--hide-controls')
          }, 1000)
        }
      } else {
        functionToExecuteOnPlayer = function undoCaptionPreviewPreSettings() {
          /* eslint-disable no-undef */
          const bigPlayButton = document.querySelector(
            '[data-plyr="play"].plyr__control.plyr__control--overlaid'
          )
          if (bigPlayButton) bigPlayButton.style.visibility = ''
        }
      }

      const functionString = functionToExecuteOnPlayer.toString()
      const messageType = 'run_internal_methods'
      const message = {
        type: messageType,
        function: functionString,
        parameter: false,
      }
      const iframe = document.querySelector('iframe')
      iframe.contentWindow.postMessage(message, '*')
    },
    togglePlatform(platform) {
      this.pixelData = {
        id: '',
        type: '',
        name: '',
        conditionType: '',
        conditionValue: '',
        script: '',
      }
      this.pixelData.type = platform
      switch (platform) {
        case 'meta':
          this.isActiveFbPixelBtn = true
          this.isActiveGooglePixelBtn = false
          this.isActiveCustomPixelBtn = false
          break
        case 'googleAds':
          this.isActiveFbPixelBtn = false
          this.isActiveGooglePixelBtn = true
          this.isActiveCustomPixelBtn = false
          break
        default:
          this.isActiveFbPixelBtn = false
          this.isActiveGooglePixelBtn = false
          this.isActiveCustomPixelBtn = true
      }
    },
    resetPixelForm() {
      this.isActiveFbPixelBtn = false
      this.isActiveGooglePixelBtn = false
      this.isActiveCustomPixelBtn = false
      this.errorMessage = ''
      this.pixelData = {
        id: '',
        type: '',
        name: '',
        conditionType: '',
        conditionValue: '',
        script: '',
      }
      this.indexPixel = null
    },
    validateField() {
      if (!this.pixelData.id) {
        this.errorMessage = this.$t('message.requiredField')
      } else {
        this.errorMessage = ''
      }
    },
    initPixel(config) {
      const pixels = []
      const pixelTypes = [
        { key: 'fbPixel', type: 'meta' },
        { key: 'gtagIds', type: 'googleAds' },
        { key: 'customPixels', type: 'customPixel' },
      ]

      pixelTypes.forEach(({ key, type }) => {
        if (Array.isArray(config[key])) {
          config[key].forEach((id) => {
            pixels.push({ id: id || '', type })
          })
        }
      })
      this.pixels = pixels
    },
    async readPlayer(loading = true) {
      this.loading = loading
      try {
        const ret = await playerApi.getPlayer(this.listQuery)
        this.loading = false
        this.subtitlesColor.foreground = ret.data.config.captionsColor.substr(
          0,
          7
        )
        this.subtitlesColor.background =
          ret.data.config.captionsBackgroundColor.substr(0, 7)
        this.subtitleFontSize = this.getFontSizeObject(
          ret.data.config.captionsFontSize
        )
        this.initPixel(ret.data.config)
        this.controlsColor = ret.data.config.controlsColor
        this.primaryColor = ret.data.config.primaryColor
        this.saveProgress = ret.data.config.saveProgress
        this.fbPixel = ret.data.config.fbPixel
        this.gtagIds = ret.data.config.gtagIds
        this.refCode = ret.data.config.refCode
        this.pandaBranding = ret.data.config.pandaBranding
        this.controls = ret.data.config.controls
        this.language = ret.data.config.defaultLanguage || ''
        this.initialPlayer = {
          playerControlsColor: ret.data.config.controlsColor,
          playerPrimaryColor: ret.data.config.primaryColor,
          playerControls: ret.data.config.controls,
          playerSaveProgress: ret.data.config.saveProgress,
          playerFbPixel: ret.data.config.fbPixel,
          playerGtagIds: ret.data.config.gtagIds,
          playerRefCode: ret.data.config.refCode,
          pandaBranding: ret.data.config.pandaBranding,
          defaultLanguage: ret.data.config.defaultLanguage,
          captionsFontSize: this.subtitleFontSize,
          captionTextColor: this.subtitlesColor.foreground,
          captionBgColor: this.subtitlesColor.background,
        }
        this.formDisable = !this.pandaBranding
        this.unsavedChanges = false
        this.UPDATE_USER_PANDA_BRANDING_STATE(this.pandaBranding)
        return ret
      } catch (e) {
        console.log(e)
        this.loading = false
      }
    },
    async readProfile() {
      try {
        const ret = await profileApi.getProfile()
        this.arrayProfiles = ret.data.map((element) => element.name)
        this.arrayProfiles.push('+ Novo Profile')
      } catch (e) {
        console.log(e)
      }
    },
    handleEndTimeEvent(timeString) {
      this.pixelData.conditionValue = this.formatTimeToSeconds(timeString)
    },
    formatTimeToSeconds(time) {
      if (!time) return 0
      let res = time.split(':').reduce((acc, time) => 60 * acc + +time)
      return res
    },
    async handleChangePixelTimeMode(timeType) {
      if (timeType !== this.pixelData.conditionType) {
        this.loadings.loadingConditionValueIpt = true
        if (
          timeType === 'percentage' &&
          this.pixelData.conditionType === 'absolute'
        ) {
          let seconds = this.getConditionAbsoluteSecondsValue()
          if (!seconds) {
            this.pixelData.conditionValue = 0
          } else {
            this.pixelData.conditionValue = Math.round(
              (seconds / this.timeVideo) * 100
            )
          }
        } else if (
          timeType === 'absolute' &&
          this.pixelData.conditionType === 'percentage'
        ) {
          this.pixelData.conditionValue = this.formatSecondsToTime(
            Math.round((this.pixelData.conditionValue * this.timeVideo) / 100)
          )
        }
        this.loadings.loadingConditionValueIpt = false
      }
      this.$nextTick(() => {
        this.pixelData.conditionType = timeType
      })
    },
    getScriptWithoutScriptTag(pixel) {
      const pixelFormatted = pixel
        .replaceAll('<script>', '')
        .replaceAll(`<\/script>`, '') // eslint-disable-line
      return pixelFormatted
    },
    async handleEditPlayer(disableLogo) {
      this.loadingButtonSave = true
      let controlsToApi = this.controls
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
        'smartSearch',
      ]
      controlsToApi.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      const regexAffiliateCode = this.getAffiliateCodeFromURL(
        'ref',
        this.refCode
      )
      try {
        const apiPlayer = {
          controls: controlsToApi,
          primaryColor: this.primaryColor,
          controlsColor: this.controlsColor,
          saveProgress: this.saveProgress,
          captionsColor: this.subtitlesColor.foreground,
          captionsBackgroundColor: this.subtitlesColor.background,
          captionsFontSize: this.pixelFontSizes[this.subtitleFontSize],
          pandaBranding: this.pandaBranding,
          fbPixel: this.fbPixel,
          gtagIds: this.gtagIds,
          refCode: regexAffiliateCode,
          defaultLanguage: this.language,
          customPixels: this.customPixels,
        }
        if (disableLogo) {
          apiPlayer.pandaBranding = false
        }
        await playerApi.editPlayer({ ...apiPlayer })
        if (!disableLogo) {
          this.readPlayer(false)
        }
        this.wasChangedSomething = false
        this.updateSnackbar()
      } catch (e) {
        console.log(e)
      }
      this.unsavedChanges = false
      this.loadingButtonSave = false
    },
    async handleEnablingPandaBrandingBonus() {
      const disableLogo = true
      this.loadingBtnModalAction = true
      await this.handleEditPlayer(disableLogo)
      this.pandaBranding = true
      await this.handleEditPlayer()
      this.UPDATE_USER_LOGO_BONUS(true)
      this.pandaBrandingAction = null
      this.openLogoActionModal = false
      this.loadingBtnModalAction = false
    },
    getFontSizeObject(size) {
      size = Number(size)
      if (size < 20) {
        return this.fontSizes[0].value
      } else if (size === 20 || !size) {
        return this.fontSizes[1].value
      }
      return this.fontSizes[2].value
    },
    unmask(text) {
      const defaultDelimiters = /[-!$%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/
      return text
        ? String(text).replace(new RegExp(defaultDelimiters, 'g'), '')
        : text
    },

    changePlayer() {
      this.checkChanges()
      if (!this.hasEditVideoPerm) return
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'colors',
          parameter: {
            colors: {
              primaryColor: this.primaryColor,
              controlsColor: this.controlsColor,
              captionsColor: this.subtitlesColor.foreground,
              captionsBackgroundColor: this.subtitlesColor.background,
            },
          },
        },
        '*'
      )
      return
    },
    setFbPixel() {
      this.unsavedChanges = true
    },
    setIDPixel() {
      this.errorMessage = ''
      this.unsavedChanges = true
    },
    setGtagIds() {
      this.unsavedChanges = true
    },
    setRefCode() {
      this.unsavedChanges = true
    },
    handleRefCodeFormat() {
      const pattern = /^[a-z0-9_-]*$/
      this.validRefCodeFormat = pattern.test(this.refCode)
      this.checkChanges()
    },
    getAffiliateCodeFromURL(parameterName, url) {
      if (!url) return ''
      parameterName = parameterName.replace(/[[\]]/g, '\\><')
      let regularExpressionGetRef = new RegExp(
          '[?&]' + parameterName + '(=([^&#]*)|&|#|$)'
        ),
        resultGetRef = regularExpressionGetRef.exec(url)
      if (!resultGetRef) return this.refCode
      if (!resultGetRef[2]) return ''
      return decodeURIComponent(resultGetRef[2].replace(/\+/g, ''))
    },
    checkChanges() {
      const playerChanged = {
        playerControls: this.controls,
        playerPrimaryColor: this.primaryColor,
        playerControlsColor: this.controlsColor,
        playerSaveProgress: this.saveProgress,
        playerFbPixel: this.fbPixel,
        playerGtagIds: this.gtagIds,
        playerRefCode: this.refCode,
        pandaBranding: this.pandaBranding,
        defaultLanguage: this.language,
        captionsFontSize: this.subtitleFontSize,
        captionTextColor: this.subtitlesColor.foreground,
        captionBgColor: this.subtitlesColor.background,
      }
      const array1 = playerChanged.playerControls
      const array2 = this.initialPlayer.playerControls
      const is_same =
        array1.length == array2.length &&
        array1.every(function (element) {
          if (array2.indexOf(element) > -1) {
            return (element = array2[array2.indexOf(element)])
          }
        })
      this.wasChangedSomething =
        is_same &&
        playerChanged.playerPrimaryColor ==
          this.initialPlayer.playerPrimaryColor &&
        playerChanged.playerControlsColor ==
          this.initialPlayer.playerControlsColor &&
        playerChanged.playerSaveProgress ==
          this.initialPlayer.playerSaveProgress &&
        playerChanged.playerFbPixel == this.initialPlayer.playerFbPixel &&
        playerChanged.playerGtagIds == this.initialPlayer.playerGtagIds &&
        playerChanged.playerRefCode == this.initialPlayer.playerRefCode &&
        playerChanged.defaultLanguage == this.initialPlayer.defaultLanguage &&
        playerChanged.captionsFontSize == this.initialPlayer.captionsFontSize &&
        playerChanged.captionTextColor == this.initialPlayer.captionTextColor &&
        playerChanged.captionBgColor == this.initialPlayer.captionBgColor &&
        playerChanged.pandaBranding == this.initialPlayer.pandaBranding
          ? false
          : true
      return this.wasChangedSomething
    },
    handleOriginalSettings() {
      this.loadingButtonOriginalSettings = true
      this.controls = [
        'play-large',
        'play',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'smartSearch',
      ]
      this.primaryColor = '#4874F1'
      this.controlsColor = '#FFFFFF'
      this.subtitlesColor.foreground = 'FFFFFF'
      this.subtitlesColor.background = '000000'
      this.subtitleFontSize = 'normal'
      this.saveProgress = true
      this.pandaBranding = true
      this.loadingButtonOriginalSettings = false
      this.language = this.$i18next.resolvedLanguage
    },
    discardChanges() {
      this.unsavedChanges = false
      this.$router.push({ name: this.to.name })
    },
    handleClose() {
      this.modalConfirmationChanges = false
      this.modalConfirmationOriginalSettings = false
    },
    changeColors() {
      this.unsavedChanges = true
    },
    handleEvent(event) {
      const e = event
      if (e.key) {
        e.key === 'Escape' && this.handleClose()
      }
      return
    },
    organizeControls() {
      let controlsToApi = this.controls
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
        'smartSearch',
      ]
      controlsToApi.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      return this.controls
    },
    handlePandaBrandingAction(e) {
      this.pandaBrandingAction = e
    },
    checkPandaBrandingChanges() {
      const hasChanges = this.pandaBranding !== this.initialPlayer.pandaBranding
      if (hasChanges) return (this.openLogoActionModal = true)
      this.handleEditPlayer()
    },
    handleCancelPandaBrandingChanges() {
      this.openLogoActionModal = false
      this.pandaBranding = this.initialPlayer.pandaBranding
      this.pandaBrandingAction = null
    },
    updateheaderTitle() {
      const backslashColor = this.$vuetify.theme.dark ? '#52577A' : '#D0D1D9'
      const title = i18n.t('message.player')
      const subtitle = i18n.t('message.ai.subtitles.default')
      const newHeaderTitleHtml = `<div class="body-bold-small">${title}<span style="color: ${backslashColor};"> / </span><span class="body-regular-small">${subtitle}</span></div>`
      const headerTitleDiv = document.querySelector('.header__title')
      if (!headerTitleDiv) return
      headerTitleDiv.innerHTML = newHeaderTitleHtml
      clearInterval(this.updateheaderTitleInterval)
    },
    updateSnackbar() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: this.$i18next.t('message.changesSaved'),
        type: 'success',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
:root {
  --plyr-color-main: #fafafa;
  --plyr-captions-text-color: #fafafa;
  --plyr-captions-background: #fafafa;
  --plyr-video-control-color: #fafafa;
  --plyr-captions-text-color: #fafafa;
}
.icon-neutral-40-light {
  color: varFigma(neutral-40-light);
}
.menu__color {
  &:hover {
    background-color: varFigma(neutral-99);
  }
}
.border-slate {
  height: 16px !important;
  width: 17px !important;
  border: varFigma(neutral-30-base) 1px solid;
}
.describeHexa {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 10%;
  color: varFigma(neutral-40-light);
  margin-right: 130px;
  width: 100px;
  display: flex;
  justify-content: flex-end;
}
.colorTitle {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: varFigma(neutral-20-dark);
}
.describeImpact {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: varFigma(neutral-40-light);
}

.player-subtitle {
  margin-bottom: 8px;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  color: varFigma(neutral-20-light);
}

.bg-dark .player-subtitle {
  color: $neutral-20-dark-dark;
}
.text-input-pixel::v-deep {
  & .v-input__control .v-text-field__details {
    padding: 0 !important;
  }
}
.grid {
  display: grid;
  grid-template-areas: 'preview' 'control' 'spacer' 'footer';
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  column-gap: 0;
  row-gap: 0;

  box-shadow: none !important;

  overflow: hidden;
  height: 100%;

  @media (min-width: 1200px) {
    grid-template-areas:
      'preview control'
      'preview spacer'
      'preview footer';
    grid-template-columns: 50% auto;
    grid-template-rows: auto 1fr auto;
  }

  &__preview {
    grid-area: preview;
    overflow: hidden;
    padding: $spacing-10;
    border-left: 1px solid varFigma(neutral-95-lightest) !important;

    &--container {
      margin: 0 auto;
      width: 75vw;

      @media (min-width: 760px) {
        width: 50vw;
      }

      @media (min-width: 1200px) {
        width: 100%;
      }
    }
  }

  &__control {
    grid-area: control;

    overflow-y: auto;
    overflow-x: hidden;

    height: 100%;
    padding: $spacing-10 $spacing-09 0 $spacing-09;
    margin-right: 2px;

    @media (min-width: 1200px) {
      border-left: 1px solid varFigma(neutral-95-lightest) !important;
    }
    @media (max-width: 1199px) {
      border-top: 1px solid varFigma(neutral-95-lightest) !important;
    }

    &--items {
      display: grid;
      padding-top: 0 !important;
    }
    &--item {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px !important;
      &:hover {
        background-color: varFigma(neutral-99);
      }
      & .v-input::v-deep {
        padding: 0 !important;
        & .v-input--selection-controls__input {
          margin-right: 0 !important;
        }
      }
    }
  }

  &__footer {
    grid-area: footer;

    @media (width >= 1200px) {
      border-left: 1px solid varFigma(neutral-95-lightest) !important;

      & .actions-row {
        margin-right: 100px;
      }
    }
    @media (width < 1200px) {
      & .actions-row {
        .spacer {
          display: none;
        }

        justify-content: center;
      }
    }
    & .actions-row {
      padding: $spacing-07 0;
    }
    &.actions-column {
      .divider-div {
        padding: 0 $spacing-10;
      }
    }
  }

  &__spacer {
    grid-area: spacer;
    @media (width >= 1200px) {
      border-left: 1px solid varFigma(neutral-95-lightest) !important;
    }
  }

  &__resources {
    &--items {
      display: grid;
      padding-top: 0 !important;
    }
    &--item {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px !important;
      &:hover {
        background-color: varFigma(neutral-99);
        &.affiliate {
          background-color: inherit;
        }
      }
      & .v-input::v-deep {
        padding: 0 !important;
        & .v-input--selection-controls__input {
          margin-right: 0 !important;
        }
      }
    }
    &--label {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      .body-regular-smaller {
        color: varFigma(neutral-40-light);
      }
    }
  }
}

.bg-dark {
  .player-subtitle {
    color: $color-neutral-white !important;
  }
}

.nowrap {
  text-wrap: nowrap;
}

.btn-width {
  &--action {
    width: 100px;
  }
  &--cancel {
    width: 100px;
  }
}

.dotsRow {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dotsActive {
  border-radius: var(--spacing-02, 4px);
  background: var(--neutral-90, #e3e4e8);
}

.affiliate-text-input::v-deep {
  max-width: 40%;
  &.v-input--is-disabled:not(.v-input--is-readonly) {
    pointer-events: initial;
    border-color: varFigma(neutral-90);
    color: varFigma(neutral-70);
  }
  & .v-input__slot input::placeholder {
    @media (width >= 1400px) and (width < 1500px) {
      font-size: 14px;
    }
    @media (width > 1200px) and (width < 1400px) {
      font-size: 12px;
    }
  }
}

.min-with-btn {
  min-width: 120px;
}

.base-modal__plyr-page {
  & header {
    height: unset !important;
    padding: $spacing-07 $spacing-05 !important;
    &::v-deep .v-toolbar__content {
      height: unset !important;
      padding: 0 $spacing-03 !important;
      & .v-toolbar__items button {
        justify-content: flex-end !important;
        & .theme--dark {
          color: #666d99 !important;
        }
        & .theme--light {
          color: #abadba !important;
        }
      }
    }
  }
  &::v-deep .v-card__text {
    padding: 0 $spacing-05 !important;
    & .v-card__text {
      padding: 0 $spacing-03 !important;
    }
  }
  &::v-deep .v-card__actions {
    padding: $spacing-07 !important;
    & .base-button.base-button--tertiary.base-button--default {
      margin-right: $spacing-03 !important;
    }
  }
}
.base-button.panda-menu-action.--pixel {
  display: flex;
  justify-content: center;
  font-size: 24px;
  width: 40px !important;
}
</style>

<style lang="scss">
.plyr-page-view-content-css {
  overflow-y: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  @media (min-width: 1200px) {
    background: linear-gradient(
      to right,
      varFigma(neutral-background) 50%,
      varFigma(neutral-background) 50%,
      varFigma(neutral-100-white) 50%,
      varFigma(neutral-100-white) 100%
    );
  }
}
.plyr-page-view-header-css {
  border-bottom: 1px solid varFigma(neutral-95-lightest) !important;
  border-left: 1px solid varFigma(neutral-95-lightest) !important;
  height: 56px !important;
  & header {
    min-height: 100% !important;
  }
}
.plyr-page-view-container-css {
  grid-template-rows: 56px 1fr !important;
}
</style>
